import * as Y from 'yjs'
import { WebsocketProvider } from 'y-websocket'
import { docSyncSocketUrl } from '@/shared/config/endpoints'
import type { DocumentParagraph } from './word'
import type { WorkbookSheetSnapshot } from './excel'

/**
 * Mirrors the Word document to ramp-ai-service over Yjs so the agent's read
 * tools can page through documents of any size (see LARGE_DOCUMENT_PLAN.md).
 * One-way sync: this client is the sole writer, Word stays the source of
 * truth, and the server replica is ephemeral (rebuilt by resync on reconnect).
 */

/**
 * Sync lifecycle logs (connect / fully-synced / close) are ALWAYS on: they are
 * rare, and connection problems are exactly what needs debugging in sideloaded
 * production builds where import.meta.env.DEV is false. Per-push data logs are
 * verbose-only: enable with DEV or `localStorage.rampAiDebug = '1'`.
 */
export const docSyncVerbose: boolean =
  import.meta.env.DEV || window.localStorage.getItem('rampAiDebug') === '1'

export function docSyncLog(...args: unknown[]): void {
  console.log('[ramp-ai:sync]', ...args)
}

/**
 * Mirror entry: paragraph text, built-in style when informative, table nesting
 * depth when inside a table, and the exact list marker + level when the
 * paragraph is a list item (numbered vs bulleted is only knowable from the
 * marker — both styles report "ListParagraph").
 */
export interface MirrorParagraph {
  t: string
  s?: string
  tb?: number
  li?: string
  lv?: number
  al?: 'center' | 'right' | 'justify'
  b?: 1
  i?: 1
  u?: 1
}

/**
 * Styles that say nothing useful: Normal is the default, Other is an opaque
 * custom style. Everything else (HeadingN, ListParagraph, Quote, …) is
 * mirrored so the agent can reproduce the structure in Markdown when it
 * replaces content — otherwise that formatting is silently lost on apply.
 */
const NON_INFORMATIVE_STYLE = /^(Normal|Other)$/

const AUTH_CLOSE_CODE = 4401
const MAX_AUTH_FAILURES = 3

/**
 * Reconnects are NOT infinite: up to MAX_RECONNECT_ATTEMPTS closes in a row
 * (≈2 minutes with the backoff ceiling), then the session suspends instead of
 * hammering a dead backend forever. The hook re-arms a fresh round on document
 * activity (selection/focus), so a long-lived pane recovers after an outage.
 */
const MAX_RECONNECT_ATTEMPTS = 12
const MAX_BACKOFF_MS = 10000

/** Per-sheet metadata mirrored alongside the row maps (Excel). */
interface SheetMeta {
  index: number
  rowCount: number
  columnCount: number
  mirroredRows: number
  mirroredColumns: number
  truncated: boolean
}

function sheetMetaEqual(a: SheetMeta | undefined, b: SheetMeta): boolean {
  return (
    a !== undefined &&
    a.index === b.index &&
    a.rowCount === b.rowCount &&
    a.columnCount === b.columnCount &&
    a.mirroredRows === b.mirroredRows &&
    a.mirroredColumns === b.mirroredColumns &&
    a.truncated === b.truncated
  )
}

function rowEqual(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((cell, i) => cell === b[i])
}

function toMirrorEntry(paragraph: DocumentParagraph): MirrorParagraph {
  const { text, style, tableNestingLevel, listMarker, listLevel } = paragraph
  const entry: MirrorParagraph = { t: text }
  if (style && !NON_INFORMATIVE_STYLE.test(style)) entry.s = style
  if (tableNestingLevel && tableNestingLevel > 0) entry.tb = tableNestingLevel
  if (listMarker !== undefined) {
    entry.li = listMarker
    if (listLevel) entry.lv = listLevel
  }
  if (paragraph.alignment) entry.al = paragraph.alignment
  if (paragraph.bold) entry.b = 1
  if (paragraph.italic) entry.i = 1
  if (paragraph.underline) entry.u = 1
  return entry
}

function entriesEqual(a: MirrorParagraph, b: MirrorParagraph): boolean {
  return (
    a.t === b.t &&
    a.s === b.s &&
    a.tb === b.tb &&
    a.li === b.li &&
    a.lv === b.lv &&
    a.al === b.al &&
    a.b === b.b &&
    a.i === b.i &&
    a.u === b.u
  )
}

/** crypto.randomUUID needs a recent webview; older Mac Word lacks it. */
function newSessionId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

export class DocSyncSession {
  readonly sessionId: string
  private readonly doc: Y.Doc
  private readonly provider: WebsocketProvider
  private readonly connectTimer: number
  private networkFailures = 0
  private suspended = false
  private authBlocked = false

  constructor(token: string) {
    this.sessionId = newSessionId()
    this.doc = new Y.Doc()
    this.provider = new WebsocketProvider(docSyncSocketUrl, this.sessionId, this.doc, {
      // The session id is unique per pane, so cross-tab BroadcastChannel sync
      // can only ever be a no-op — skip it.
      disableBc: true,
      connect: false,
      maxBackoffTime: MAX_BACKOFF_MS,
      params: { token }
    })
    // React 18 StrictMode mounts effects twice in dev; deferring the first
    // connect by a tick means the throwaway session never opens a socket
    // (avoids "WebSocket is closed before the connection is established").
    this.connectTimer = window.setTimeout(() => this.provider.connect(), 0)

    docSyncLog(`session ${this.sessionId} connecting to ${docSyncSocketUrl}`)
    this.provider.on('status', (event: { status: string }) => {
      docSyncLog(`session ${this.sessionId} ${event.status}`)
      if (event.status === 'connected') this.networkFailures = 0
    })
    this.provider.on('sync', (synced: boolean) => {
      docSyncLog(
        synced
          ? `session ${this.sessionId} FULLY SYNCED — server has the document`
          : `session ${this.sessionId} sync lost — server state stale until resync`
      )
    })

    // A revoked/expired token makes the server close with 4401 on every
    // attempt; that gives up PERMANENTLY (only a token change / remount can
    // fix it). Other failures count toward the bounded reconnect budget.
    // Either way the snapshot falls back to truncated documentText.
    let authFailures = 0
    this.provider.on('connection-close', (event: { code?: number; reason?: string } | null) => {
      docSyncLog(`session ${this.sessionId} connection closed`, event?.code ?? '', event?.reason ?? '')
      if (event?.code === AUTH_CLOSE_CODE) {
        authFailures += 1
        if (authFailures >= MAX_AUTH_FAILURES) {
          docSyncLog(`session ${this.sessionId} giving up after ${authFailures}× auth rejections — falling back to snapshot context`)
          this.authBlocked = true
          this.provider.disconnect()
        }
        return
      }
      authFailures = 0
      this.networkFailures += 1
      if (this.networkFailures >= MAX_RECONNECT_ATTEMPTS) {
        this.suspended = true
        docSyncLog(
          `session ${this.sessionId} suspending after ${this.networkFailures} failed connection attempts — ` +
            'will retry on next document activity'
        )
        this.provider.disconnect()
      }
    })
  }

  /**
   * Re-arm a suspended session (called from the document activity triggers —
   * selection change / pane focus): one fresh bounded round of reconnects.
   */
  resumeIfSuspended(): void {
    if (!this.suspended) return
    this.suspended = false
    this.networkFailures = 0
    docSyncLog(`session ${this.sessionId} retrying connection after document activity`)
    this.provider.connect()
  }

  /** True once the initial sync handshake completed — the server has the doc. */
  get connected(): boolean {
    return this.provider.wsconnected && this.provider.synced
  }

  /** Coarse state for the pane's sync indicator. */
  get state(): 'connecting' | 'synced' | 'offline' {
    if (this.authBlocked || this.suspended) return 'offline'
    return this.connected ? 'synced' : 'connecting'
  }

  /**
   * Fires when connectivity/sync state changes, so the page-context snapshot
   * can be rebuilt slim as soon as the mirror is ready (and fall back to the
   * truncated text if the connection drops). Cleaned up by destroy().
   */
  onStatusChange(listener: () => void): void {
    this.provider.on('status', listener)
    this.provider.on('sync', listener)
  }

  /**
   * Publish the LIVE selection as Yjs awareness state — ephemeral presence
   * data, not document history. Page context still carries the send-time
   * selection (the intent anchor); awareness lets the agent re-check what is
   * selected right now via the getCurrentOfficeSelection tool. The provider
   * renews awareness automatically while connected and clears it on destroy.
   */
  publishSelection(host: string, text?: string, address?: string): void {
    const hasText = text !== undefined && text.trim() !== ''
    if (!hasText && !address) {
      this.provider.awareness.setLocalStateField('selection', null)
      return
    }
    this.provider.awareness.setLocalStateField('selection', {
      host,
      ...(hasText ? { text: text.slice(0, 2000) } : {}),
      ...(address ? { address } : {}),
      capturedAt: new Date().toISOString()
    })
  }

  /**
   * Mirror the latest workbook read (Excel). Model in the shared doc:
   *  - Y.Map('workbook'): sheet name -> { index, rowCount, columnCount,
   *    mirroredRows, mirroredColumns, truncated }
   *  - Y.Map('sheet:<name>'): row index (string, 0-based) -> string[] of
   *    formatted cell text.
   * Row-keyed maps mean an edit re-syncs only the changed rows, and mirrored
   * indexes always equal real sheet coordinates (windowed from A1).
   */
  pushWorkbook(sheets: WorkbookSheetSnapshot[]): void {
    const meta = this.doc.getMap<SheetMeta>('workbook')
    let changedRows = 0
    this.doc.transact(() => {
      const seen = new Set<string>()
      sheets.forEach((sheet, index) => {
        seen.add(sheet.name)
        const entry: SheetMeta = {
          index,
          rowCount: sheet.rowCount,
          columnCount: sheet.columnCount,
          mirroredRows: sheet.text.length,
          mirroredColumns: sheet.text[0]?.length ?? 0,
          truncated: sheet.truncated
        }
        if (!sheetMetaEqual(meta.get(sheet.name), entry)) meta.set(sheet.name, entry)

        const rows = this.doc.getMap<string[]>(`sheet:${sheet.name}`)
        sheet.text.forEach((row, rowIndex) => {
          const key = String(rowIndex)
          const previous = rows.get(key)
          if (!previous || !rowEqual(previous, row)) {
            rows.set(key, row)
            changedRows += 1
          }
        })
        for (const key of [...rows.keys()]) {
          if (Number(key) >= sheet.text.length) rows.delete(key)
        }
      })
      for (const name of [...meta.keys()]) {
        if (!seen.has(name)) {
          meta.delete(name)
          const rows = this.doc.getMap<string[]>(`sheet:${name}`)
          for (const key of [...rows.keys()]) rows.delete(key)
        }
      }
    })
    if (docSyncVerbose) {
      docSyncLog(`pushed workbook: ${sheets.length} sheets, ${changedRows} changed rows, connected=${this.connected}`)
    }
  }

  /** One line per sheet for the page-context snapshot — a workbook map. */
  workbookOutline(): string | undefined {
    const meta = this.doc.getMap<SheetMeta>('workbook')
    if (meta.size === 0) return undefined
    const entries: Array<{ name: string } & SheetMeta> = []
    meta.forEach((value, name) => entries.push({ name, ...value }))
    entries.sort((a, b) => a.index - b.index)
    return entries
      .map((sheet) => {
        const window = sheet.truncated ? ` (mirrored ${sheet.mirroredRows}×${sheet.mirroredColumns})` : ''
        return `${sheet.name} — ${sheet.rowCount} rows × ${sheet.columnCount} cols${window}`
      })
      .join('\n')
  }

  /**
   * Mirror the latest full paragraph read (Word). Only the changed span
   * (common prefix/suffix trimmed) is rewritten, so steady-state editing
   * produces small deltas even on huge documents. Safe to call while
   * disconnected: Yjs accumulates local updates and the provider resyncs
   * on reconnect.
   */
  pushParagraphs(paragraphs: DocumentParagraph[]): void {
    const next = paragraphs.map(toMirrorEntry)
    const array = this.doc.getArray<MirrorParagraph>('paragraphs')
    const current = array.toArray()

    let start = 0
    while (start < current.length && start < next.length && entriesEqual(current[start], next[start])) {
      start += 1
    }
    let endCurrent = current.length
    let endNext = next.length
    while (endCurrent > start && endNext > start && entriesEqual(current[endCurrent - 1], next[endNext - 1])) {
      endCurrent -= 1
      endNext -= 1
    }
    if (start === endCurrent && start === endNext) return

    this.doc.transact(() => {
      if (endCurrent > start) array.delete(start, endCurrent - start)
      if (endNext > start) array.insert(start, next.slice(start, endNext))
    })
    if (docSyncVerbose) {
      docSyncLog(
        `pushed paragraphs ${start}–${endNext - 1} (removed ${endCurrent - start}, total ${next.length}, ` +
          `connected=${this.connected})`
      )
    }
  }

  destroy(): void {
    window.clearTimeout(this.connectTimer)
    this.provider.destroy()
    this.doc.destroy()
  }
}
